import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Building2, Phone, MapPin, Save, Loader2, 
  Edit2, X, Image as ImageIcon, Sparkles, Check, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PartnerLayout from '../../components/partner/PartnerLayout';
import CloudinaryUploader from '../../components/CloudinaryUploader';

const PartnerProfilePage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [benefits, setBenefits] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  
  const [formData, setFormData] = useState({
    business_name: '',
    phone: '',
    address: '',
    logo_url: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const [partnerRes, benefitsRes, redemptionsRes] = await Promise.all([
        api.get('/partners/me', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/partners/me/benefits', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/partners/redemptions', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPartner(partnerRes.data);
      setBenefits(benefitsRes.data || []);
      setRedemptions(redemptionsRes.data?.redemptions || []);
      
      setFormData({
        business_name: partnerRes.data.business_name || '',
        phone: partnerRes.data.phone || '',
        address: partnerRes.data.address || '',
        logo_url: partnerRes.data.logo_url || ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('partner_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('partner_token');
      await api.put('/partners/me/update', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setPartner(prev => ({ ...prev, ...formData }));
      localStorage.setItem('partner_data', JSON.stringify({ ...partner, ...formData }));
      
      setEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (result) => {
    setFormData(prev => ({ ...prev, logo_url: result.url }));
    toast.success('Logo subido correctamente');
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: '' }));
  };

  const approvedCount = benefits.filter(b => b.status === 'approved').length;
  const pendingCount = benefits.filter(b => b.status === 'pending').length;

  if (loading) {
    return (
      <PartnerLayout currentPage="perfil">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout currentPage="perfil">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-black text-white">Mi Perfil</h1>
          <p className="text-zinc-400">Información de tu negocio</p>
        </div>
        {!editing ? (
          <Button 
            onClick={() => setEditing(true)} 
            variant="outline"
            className="border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
            data-testid="edit-profile-btn"
          >
            <Edit2 size={16} className="mr-2" />
            Editar Perfil
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button 
              onClick={() => {
                setEditing(false);
                setFormData({
                  business_name: partner?.business_name || '',
                  phone: partner?.phone || '',
                  address: partner?.address || '',
                  logo_url: partner?.logo_url || ''
                });
              }} 
              variant="ghost"
              className="text-zinc-400"
            >
              <X size={16} className="mr-1" />
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} className="mr-1" />}
              Guardar
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
        >
          {/* Profile Header */}
          <div className="flex items-center gap-5 mb-8 pb-6 border-b border-zinc-800">
            {formData.logo_url ? (
              <img 
                src={formData.logo_url} 
                alt={partner?.business_name} 
                className="w-20 h-20 rounded-xl object-cover border border-zinc-700" 
              />
            ) : (
              <div className="w-20 h-20 bg-zinc-800 rounded-xl flex items-center justify-center border border-zinc-700">
                <Building2 size={36} className="text-zinc-600" />
              </div>
            )}
            <div>
              <h3 className="font-bold text-xl text-white">{partner?.business_name}</h3>
              <p className="text-zinc-500 text-sm">@{partner?.username}</p>
            </div>
          </div>

          {/* Logo Upload Section - Only in edit mode */}
          {editing && (
            <div className="mb-6">
              <Label className="text-sm font-medium text-zinc-400 mb-3 block flex items-center gap-2">
                <ImageIcon size={16} />
                Logo del Negocio
              </Label>
              
              {formData.logo_url ? (
                <div className="relative inline-block">
                  <img 
                    src={formData.logo_url} 
                    alt="Logo" 
                    className="w-32 h-32 rounded-xl object-cover border-2 border-zinc-700"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                    data-testid="remove-logo-btn"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500/90 text-white px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                    <Check size={12} />
                    Guardado
                  </div>
                </div>
              ) : (
                <CloudinaryUploader
                  onUploadComplete={handleLogoUpload}
                  folder="bravo/partners"
                  resourceType="image"
                  maxSize={5 * 1024 * 1024}
                  accept="image/jpeg,image/png,image/webp"
                  buttonText="Subir logo"
                  showPreview={false}
                  className="rounded-xl"
                />
              )}
              
              <p className="text-zinc-600 text-xs mt-2">
                Recomendado: 200x200 px, formato JPG, PNG o WebP. Máx 5MB.
              </p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-medium text-zinc-400 mb-2 block">Nombre del Negocio</Label>
              {editing ? (
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  className="h-12 bg-zinc-800 border-zinc-700 text-white text-base focus:border-[#D4AF37]"
                  data-testid="business-name-input"
                />
              ) : (
                <p className="text-white text-lg">{partner?.business_name || '-'}</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-zinc-400 mb-2 block">Teléfono</Label>
              {editing ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+54 11 1234-5678"
                  className="h-12 bg-zinc-800 border-zinc-700 text-white text-base focus:border-[#D4AF37]"
                  data-testid="phone-input"
                />
              ) : (
                <p className="text-white flex items-center gap-2">
                  {partner?.phone ? (
                    <>
                      <Phone size={16} className="text-zinc-600" />
                      {partner.phone}
                    </>
                  ) : (
                    <span className="text-zinc-600">No especificado</span>
                  )}
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-zinc-400 mb-2 block">Dirección</Label>
              {editing ? (
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Av. Corrientes 1234, CABA"
                  className="h-12 bg-zinc-800 border-zinc-700 text-white text-base focus:border-[#D4AF37]"
                  data-testid="address-input"
                />
              ) : (
                <p className="text-white flex items-center gap-2">
                  {partner?.address ? (
                    <>
                      <MapPin size={16} className="text-zinc-600" />
                      {partner.address}
                    </>
                  ) : (
                    <span className="text-zinc-600">No especificado</span>
                  )}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
        >
          <h3 className="font-bold text-lg text-white mb-6 flex items-center gap-2">
            <Sparkles size={20} className="text-[#D4AF37]" />
            Estadísticas
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-800/50 rounded-xl p-5 text-center">
              <p className="text-4xl font-extrabold text-[#D4AF37]">{approvedCount}</p>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mt-1">Beneficios Activos</p>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-5 text-center">
              <p className="text-4xl font-extrabold text-yellow-500">{pendingCount}</p>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mt-1">Pendientes</p>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-5 text-center">
              <p className="text-4xl font-extrabold text-white">{redemptions.length}</p>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mt-1">Códigos Canjeados</p>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-5 text-center">
              <p className="text-white font-medium">
                {partner?.created_at 
                  ? new Date(partner.created_at).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' }) 
                  : '-'
                }
              </p>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mt-1">Miembro desde</p>
            </div>
          </div>

          {/* Category Badge */}
          {partner?.category && (
            <div className="mt-6 pt-6 border-t border-zinc-800">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Categoría</p>
              <span className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-[#D4AF37] font-medium capitalize">
                {partner.category}
              </span>
            </div>
          )}
        </motion.div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerProfilePage;
